// Pantalla `command="register"`. Port de la rama `when "register"` de
// keycloak.coffee. Hay TRES modos según data.form.mode:
//   - estándar (mode no especial)        → form de registro completo
//                                           con quality seal y bloque
//                                           de plan gratis.
//   - "refer"                            → invitación con regalo de
//                                           mes adicional.
//   - "invitation" o "accountant"        → registro de asesor o
//                                           invitado. Email read-only
//                                           si invitation.
//
// Cada uno tiene textos y layout propios. El form en sí (campos
// firstName, email, password, accepted-legal-terms, hidden
// password-confirm y mode) es prácticamente idéntico — difiere solo el
// botón final ("Empieza gratis" vs "Accede"). Mantenemos los TRES
// renderizados separados para fidelidad visual con el legacy.

import type { ChangeEvent, FormEvent } from "react";
import type { KcData } from "../kcData";
import { Input } from "../components/Input";
import { LegalConditions } from "../components/LegalConditions";
import { QualitySeal } from "../components/QualitySeal";
import { showIntercom } from "../trackers";
import { getFdPublicConfig } from "../window";
import { msg } from "../i18n";
import googleLogoUrl from "../assets/google_logo_custom.svg";
import masEficienteUrl from "../assets/mas-eficiente.png";

export interface RegisterPageProps {
  data: KcData;
  running: boolean;
  nativeApp: string | null;
  onChange: (key: string, value: string | boolean) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

function commonFooterLinks(baseUrl: string) {
  // Bloque pesado con muchos links. La message contiene el HTML completo
  // con placeholders {0}/{1} reemplazados por baseUrl. Los onClick para
  // mostrar Intercom se atan delegando en click del contenedor.
  const onContainerClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const t = e.target as HTMLElement;
    const a = t.closest("a[data-action='intercom']");
    if (a) {
      e.preventDefault();
      showIntercom();
    }
  };

  return (
    <div onClick={onContainerClick}>
      <div className="row more-details row-margin-top">
        <div className="col-md-offset-3 col-md-6 text-center">
          <p
            className="text-center"
            dangerouslySetInnerHTML={{
              __html: msg("registerFooterSupportHtml"),
            }}
          />
        </div>
      </div>
      <div className="row row-margin-top">
        <div className="col-md-offset-3 col-md-6 text-center">
          <span
            className="text-gray"
            dangerouslySetInnerHTML={{
              __html: msg("registerFooterAboutHtml", baseUrl),
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface RegisterFormProps extends RegisterPageProps {
  mode?: string;
  buttonLabel: string;
  emailReadOnly?: boolean;
  showSocial: boolean;
  // "email": paso 1 del flujo Email OTP Registration (sin contraseña,
  // postea al authenticator). "profile" (default): formulario completo.
  step?: "email" | "profile";
  // Mostrar el bloque de aceptación legal en este formulario.
  showLegal: boolean;
}

function RegisterForm(props: RegisterFormProps) {
  const {
    data,
    running,
    onChange,
    onSubmit,
    mode = "",
    buttonLabel,
    emailReadOnly = false,
    showSocial,
    step = "profile",
    showLegal,
  } = props;
  const isEmailStep = step === "email";
  const formFirstName = (data.form?.firstName as string | undefined) ?? "";
  const formEmail = (data.form?.email as string | undefined) ?? "";
  const formPassword = (data.form?.password as string | undefined) ?? "";

  return (
    <form
      id="kc-register-form"
      action={isEmailStep ? data.url?.loginAction : data.url?.registrationAction}
      method="post"
      onSubmit={onSubmit}
    >
      {showSocial && data.socialProviders?.[0] ? (
        <div className="form-group">
          <a
            className="btn btn-block btn-default btn-lg"
            style={{ paddingTop: "10px", paddingBottom: "10px" }}
            href={data.socialProviders[0].loginUrl}
          >
            <img
              src={googleLogoUrl}
              style={{ width: "24px", height: "24px" }}
              alt="Google"
            />
            {" "}
            {msg("registerWithGoogle")}
          </a>
        </div>
      ) : null}
      {showSocial ? (
        <div className="form-group">{msg("registerSocialDivider")}</div>
      ) : null}
      {/* Email OTP Registration — paso "email": solo se pide el email;
          el nombre se teclea después, en el formulario de registro. */}
      {!isEmailStep ? (
        <Input
          type="text"
          id="firstName"
          autoFocus
          name="firstName"
          autoComplete="given-name"
          value={formFirstName}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange("firstName", e.target.value)
          }
          placeholder={msg("registerFirstNamePlaceholder")}
          messagesPerField={data.messagesPerField}
        />
      ) : null}
      <Input
        type="email"
        id="email"
        name="email"
        autoComplete="username"
        autoFocus={isEmailStep}
        readOnly={emailReadOnly}
        value={formEmail}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange("email", e.target.value)
        }
        placeholder={msg("email")}
        messagesPerField={data.messagesPerField}
      />
      {/* Email OTP Registration — paso "email": sin contraseña; se
          teclea después, en el formulario de registro real. */}
      {!isEmailStep ? (
        <>
          <Input
            type="password"
            id="password"
            name="password"
            autoComplete="new-password"
            value={formPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange("password", e.target.value)
            }
            placeholder={msg("registerPasswordPlaceholder")}
            messagesPerField={data.messagesPerField}
          />
          <input
            type="hidden"
            id="password-confirm"
            name="password-confirm"
            value={formPassword}
          />
        </>
      ) : null}
      {mode ? (
        <input type="hidden" id="mode" name="mode" value={mode} />
      ) : null}
      {/* La aceptación legal se muestra donde toca aceptarla: el paso
          "email" del flujo OTP, o el formulario de registro en modo
          clásico. NO en el formulario post-OTP (ya se aceptó antes). */}
      {showLegal ? (
        <LegalConditions data={data} onChange={onChange} />
      ) : null}
      <div className="form-group">
        <button
          id="buttonSubmit"
          type="submit"
          className="btn btn-lg btn-block btn-success"
          disabled={running}
        >
          {buttonLabel}
        </button>
      </div>
    </form>
  );
}

function AlreadyAccountLink({
  data,
  nativeApp,
  includeNativeApp,
}: {
  data: KcData;
  nativeApp: string | null;
  includeNativeApp: boolean;
}) {
  const formEmail = (data.form?.email as string | undefined) ?? "";
  const href =
    data.url!.loginUrl +
    (formEmail ? "&hint_email=" + encodeURIComponent(formEmail) : "") +
    (includeNativeApp && nativeApp ? `&nativeApp=${nativeApp}` : "");
  return (
    <p>
      {msg("backToLoginText")}{" "}
      <a href={href}>{msg("accesToAccount")}</a>
    </p>
  );
}

export function RegisterPage(props: RegisterPageProps) {
  const { data, nativeApp } = props;
  const baseUrl = getFdPublicConfig().BASE_URL;
  const mode = (data.form?.mode as string | undefined) ?? "";
  // Email OTP Registration: el paso "email" es el formulario "delante"
  // del registro (sin contraseña) que pinta el authenticator.
  const isEmailStep =
    (data.form?.registrationStep as string | undefined) === "email";
  const verifiedEmail = (data.form?.verifiedEmail as string | undefined) ?? "";
  // Tras verificar el email por OTP, el formulario de registro es de
  // "completar" — sin marketing, dejando claro que se sigue un proceso.
  const isPostOtp = !isEmailStep && verifiedEmail !== "";
  // La aceptación legal va donde toca aceptarla: paso "email" del flujo
  // OTP, o el formulario de registro en modo clásico. NUNCA en el
  // formulario post-OTP (ya se aceptó en el paso "email").
  const showLegal = isEmailStep || verifiedEmail === "";
  const isStandardMode =
    mode !== "invitation" && mode !== "accountant" && mode !== "refer";

  // Paso 2 tras el OTP: "completa tu registro". Layout mínimo, sin los
  // textos de marketing, para que quede claro que se continúa el
  // proceso iniciado en el paso "email".
  if (isPostOtp && isStandardMode) {
    return (
      <div>
        <div className="container">
          <div className="row row-margin-bottom hidden-xs">
            <div className="col-sm-12 text-center">
              <h1>{msg("registerCompleteTitle")}</h1>
              <h3>{msg("registerCompleteSubtitle")}</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-lg-push-4 col-md-6 col-md-push-3 col-sm-8 col-sm-push-2 row-margin-top-half text-center">
              <div className="panel panel-default">
                <div className="panel-body text-center">
                  <RegisterForm
                    {...props}
                    step="profile"
                    showLegal={false}
                    buttonLabel={msg("doRegister")}
                    emailReadOnly
                    showSocial={false}
                  />
                </div>
              </div>
              {/* Mismo enlace de "¿Ya tienes acceso? Accede ahora" que el
                  primer formulario: permite abortar el registro y volver
                  al login. */}
              <AlreadyAccountLink
                data={data}
                nativeApp={nativeApp}
                includeNativeApp={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEmailStep || isStandardMode) {
    // Modo estándar (y el paso "email" del flujo con OTP)
    return (
      <div>
        <div className="container">
          <div className="row row-margin-bottom hidden-xs">
            <div className="col-sm-12 text-center">
              <h1>{msg("registerHeroTitle")}</h1>
              <h3>{msg("registerHeroSubtitle")}</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-lg-push-4 col-md-6 col-md-push-3 col-sm-8 col-sm-push-2 row-margin-top-half text-center">
              <div className="panel panel-default">
                <div className="panel-body text-center">
                  <RegisterForm
                    {...props}
                    step={isEmailStep ? "email" : "profile"}
                    showLegal={showLegal}
                    buttonLabel={
                      isEmailStep ? msg("doContinue") : msg("doRegister")
                    }
                    showSocial={!nativeApp}
                  />
                </div>
              </div>
              <AlreadyAccountLink
                data={data}
                nativeApp={nativeApp}
                includeNativeApp={true}
              />
            </div>
          </div>
          <div className="row row-margin-top">
            <div className="col-md-8 col-md-offset-2">
              <QualitySeal />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 text-center row-margin-bottom-half row-margin-top-half">
              <h3>{msg("freePlanTitle")}</h3>
            </div>
          </div>
          <div className="row more-details">
            <div className="col-md-8 col-md-offset-2">
              <div className="row-margin-bottom-half">
                <b>{msg("freePlanQ1")}</b>
                <p>{msg("freePlanA1")}</p>
              </div>
              <div className="row-margin-bottom-half">
                <b>{msg("freePlanQ2")}</b>
                <p>{msg("freePlanA2")}</p>
              </div>
              <div>
                <b>{msg("freePlanQ3")}</b>
                <p>{msg("freePlanA3")}</p>
              </div>
            </div>
          </div>
          {commonFooterLinks(baseUrl)}
        </div>
      </div>
    );
  }

  if (mode === "refer") {
    return (
      <div>
        <div className="container">
          <div className="row row-margin-bottom">
            <div className="col-md-12 text-center">
              <h1>{msg("referHeroTitle")}</h1>
              <div className="row-margin-top-half hidden visible-xs">
                <h3>{msg("referHeroSubtitle")}</h3>
              </div>
              <div className="row-margin-top-quarter hidden-xs">
                <h3>{msg("referHeroSubtitle")}</h3>
              </div>
            </div>
            <div className="col-md-8 col-md-offset-2">
              <div
                className="more-details text-center row-margin-top-half"
                dangerouslySetInnerHTML={{ __html: msg("referBenefitHtml") }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-lg-push-7 col-md-6 col-md-push-3 col-sm-8 col-sm-push-2 text-center">
              <div className="panel panel-default">
                <div className="panel-body text-center">
                  <RegisterForm
                    {...props}
                    mode={mode}
                    showLegal={showLegal}
                    buttonLabel={msg("doRegister")}
                    emailReadOnly={verifiedEmail !== ""}
                    showSocial={!nativeApp}
                  />
                </div>
              </div>
              <AlreadyAccountLink
                data={data}
                nativeApp={nativeApp}
                includeNativeApp={true}
              />
            </div>
            <div className="col-lg-6 col-lg-pull-3 col-lg-push-0 col-md-8 col-md-push-2 col-sm-12 row-margin-top-half row-margin-bottom hidden-xs">
              <img
                src={masEficienteUrl}
                style={{ maxWidth: "100%" }}
                alt=""
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12 text-center row-margin-bottom-half row-margin-top-half">
              <h3>{msg("referValuePropTitle")}</h3>
            </div>
          </div>
          <div className="row row-margin-bottom-half more-details">
            <div className="col-md-8 col-md-offset-2">
              <div className="row-margin-bottom-half text-center">
                <p>{msg("referValuePropP1")}</p>
              </div>
              <div className="row-margin-bottom-half text-center">
                <p>{msg("referValuePropP2")}</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 col-md-offset-3">
              <hr />
            </div>
          </div>
          {commonFooterLinks(baseUrl)}
        </div>
      </div>
    );
  }

  // mode === "invitation" || mode === "accountant"
  const readOnly = mode === "invitation";
  return (
    <div>
      <span className="parc-background-2 invitation section-background ___gradient" />
      <div className="container">
        <div className="row row-margin-bottom-half hidden-xs">
          <div className="col-sm-12 text-center">
            <h1>{msg("inviteHeroTitle")}</h1>
            {readOnly ? (
              <h3>{msg("inviteAccessSubtitle")}</h3>
            ) : (
              <h3>{msg("accountantSubtitle")}</h3>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 col-md-push-4 text-center">
            <div className="panel panel-default">
              <div className="panel-body text-center">
                <RegisterForm
                  {...props}
                  mode={mode}
                  showLegal={showLegal}
                  buttonLabel={msg("doAccess")}
                  emailReadOnly={readOnly || verifiedEmail !== ""}
                  showSocial
                />
              </div>
            </div>
            <AlreadyAccountLink
              data={data}
              nativeApp={nativeApp}
              includeNativeApp={false}
            />
          </div>
        </div>
        {commonFooterLinks(baseUrl)}
      </div>
    </div>
  );
}
